'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SpecRow {
  label: string;
  value: string;
  unit: string;
}

interface SpecSectionProps {
  id: string;
  index: string;
  category: string;
  tagline: string;
  imageUrl: string;
  imageAlt: string;
  specs: SpecRow[];
  painPoint: string;
  pdfLabel: string;
  accent?: boolean;
  showPdf?: boolean;
  painPointHighlight?: boolean;
  videoUrl?: string;
  videoCaption?: string;
  overlayLabel?: string;
}

export default function SpecSection({
  id, index, category, tagline,
  imageUrl, imageAlt, specs,
  painPoint, pdfLabel, accent = false, showPdf = false, painPointHighlight = false,
  videoUrl, videoCaption, overlayLabel,
}: SpecSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-up, .reveal-left').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 90);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const extractYouTubeId = (url: string | undefined): string | undefined => {
    if (!url) return undefined;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : undefined;
  };

  return (
    <section
      id={id}
      ref={sectionRef}
      className={`spoke-section py-20 lg:py-28 ${accent ? 'heat-bg-alt' : 'heat-bg'}`}
    >
      <div className="section-accent" />

      <div className="max-w-screen-xl mx-auto px-6 lg:px-14">
        {/* Header row */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="reveal-up">
              <span className="spoke-index">{index} // {category.toUpperCase()}</span>
            </div>
            <h2 className="reveal-up delay-1 font-sans font-900 text-[clamp(2rem,5vw,3.5rem)] leading-none tracking-tight text-bone mt-3 uppercase">
              {category}
            </h2>
            <p className="reveal-up delay-2 font-mono text-sm text-amber mt-2 tracking-wide">
              {tagline}
            </p>
          </div>
          {showPdf && (
            <div className="reveal-up delay-2 shrink-0">
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="inline-flex items-center gap-2 px-4 py-2 border border-furnace/40 text-bone-dim hover:text-bone hover:border-furnace transition-colors font-mono text-xs tracking-wide uppercase"
              >
                <Icon name="ArrowDownTrayIcon" size={14} className="text-amber" />
                {pdfLabel}
              </a>
            </div>
          )}
        </div>

        {/* Two-column: image + specs */}
        <div className="grid lg:grid-cols-2 gap-10 items-stretch">
          {/* Image or Video */}
          <div className="flex flex-col gap-6">
            {videoUrl ? (
              <div className="reveal-left relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
                {videoPlaying ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYouTubeId(videoUrl)}?autoplay=1&rel=0`}
                    title={videoCaption || 'Video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full border-0"
                    suppressHydrationWarning
                  />
                ) : (
                  <>
                    {/* Thumbnail */}
                    <img
                      src={`https://img.youtube.com/vi/${extractYouTubeId(videoUrl)}/maxresdefault.jpg`}
                      alt={videoCaption || 'Video thumbnail'}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    {/* Dark overlay */}
                    <div className="absolute inset-0 bg-black/40" />
                    {/* Circular play button */}
                    <button
                      onClick={() => setVideoPlaying(true)}
                      aria-label="Play video"
                      className="absolute inset-0 flex items-center justify-center group"
                    >
                      <span className="flex items-center justify-center w-16 h-16 rounded-full bg-white/90 group-hover:bg-white transition-all duration-200 shadow-lg group-hover:scale-110">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-7 h-7 text-gray-900 ml-1"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="reveal-left relative overflow-hidden" style={{ aspectRatio: '16/10' }}>
                <img
                  src={imageUrl}
                  alt={imageAlt}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/60 to-transparent" />
                {/* Live indicator */}
                <div className="absolute bottom-4 left-4 counter-pill">
                  <span className="font-mono text-[0.58rem] font-700 tracking-widest text-bone-dim uppercase">
                    {overlayLabel || 'Guyana apartments concept'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Spec grid + pain point */}
          <div className="flex flex-col">
            {/* Data grid */}
{specs.length > 0 && (
<div className="reveal-up delay-1 spec-grid mb-8">
{specs.map((s, i) => (
                <div key={i} className="spec-cell">
<div className="spec-cell-label">{s.label}</div>
<div className="spec-cell-value">{s.value}</div>
<div className="spec-cell-unit">{s.unit}</div>
                </div>
              ))}
            </div>
)}
            {/* Pain point */}
            <div className="flex-1" />
            <div className="reveal-up delay-2 mb-8 pl-4 border-l-2 border-furnace/40">
              <p
                className="font-sans text-[0.95rem] leading-relaxed text-bone-dim"
              >
                {painPoint}
              </p>
            </div>

            {/* CTA — pushed to bottom */}
            <div className="reveal-up delay-3 mt-auto">
              <a
                href="#contact"
                className="btn-furnace"
              >
                Contact Us
                <Icon name="ArrowRightIcon" size={14} className="text-white" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}